import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { Camera, ImagePlus, Smile, X, Hash, Crop, EyeOff } from 'lucide-react';

import { AppShell } from '@/components/AppShell';
import { CameraFlash } from '@/components/CameraFlash';
import { ImageCropper } from '@/components/ImageCropper';
import { WashiTape } from '@/components/WashiTape';
import { EmojiPicker } from '@/components/EmojiPicker';
import { EmojiSuggestions } from '@/components/EmojiSuggestions';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { useEmojiShortcode } from '@/hooks/useEmojiShortcode';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUploadPicture } from '@/hooks/useUploadPicture';
import { LoginArea } from '@/components/auth/LoginArea';
import { formatPolaroidDate, getHashtagColor } from '@/lib/polaroid-utils';
import { cn } from '@/lib/utils';

/** Generate a fake event ID for consistent visual seeding of the preview card */
function previewSeed(): string {
  return 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2';
}

export default function Upload() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutateAsync: uploadPicture, isPending } = useUploadPicture();
  const { hasBgImage } = useDittoThemeContext();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  /** The original un-cropped file — kept so re-crop works from the source */
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  /** The raw original image URL before cropping — used as the cropper source */
  const [rawImageUrl, setRawImageUrl] = useState<string | null>(null);
  /** Whether the cropper is currently active */
  const [isCropping, setIsCropping] = useState(false);
  const [caption, setCaption] = useState('');
  const [title, setTitle] = useState('');
  const [hashtags, setHashtags] = useState<string[]>([]);
  const [hashtagInput, setHashtagInput] = useState('');
  const [cwEnabled, setCwEnabled] = useState(false);
  const [cwReason, setCwReason] = useState('');
  const [flash, setFlash] = useState(false);
  const [titleEmojiOpen, setTitleEmojiOpen] = useState(false);
  const [captionEmojiOpen, setCaptionEmojiOpen] = useState(false);
  const [clearSpin, setClearSpin] = useState(false);
  const clearSpinTimeout = useRef<ReturnType<typeof setTimeout>>();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const captionInputRef = useRef<HTMLTextAreaElement>(null);
  const hashtagInputRef = useRef<HTMLInputElement>(null);

  const titleShortcode = useEmojiShortcode();
  const captionShortcode = useEmojiShortcode();

  const seed = previewSeed();
  const todayDate = formatPolaroidDate(Math.floor(Date.now() / 1000));

  useSeoMeta({
    title: 'Upload — Polaroids',
    description: 'Share a new polaroid with the world.',
  });

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/avif', 'image/apng'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a valid image file (JPEG, PNG, GIF, WebP, AVIF)');
      return;
    }

    // GIFs can't be cropped (animated), go straight to preview
    if (file.type === 'image/gif') {
      setSelectedFile(file);
      setOriginalFile(file);
      const url = URL.createObjectURL(file);
      setPreview(url);
      setRawImageUrl(null);
      setIsCropping(false);
      return;
    }

    // For all other images, enter cropping mode
    const url = URL.createObjectURL(file);
    setRawImageUrl(url);
    setSelectedFile(file);
    setOriginalFile(file);
    setPreview(null);
    setIsCropping(true);

    // Reset the file input so the same file can be re-selected
    e.target.value = '';
  }, []);

  const addHashtag = useCallback((raw: string) => {
    const tag = raw.replace(/^#/, '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
    if (tag && !hashtags.includes(tag) && hashtags.length < 5) {
      setHashtags((prev) => [...prev, tag]);
    }
    setHashtagInput('');
  }, [hashtags]);

  const removeHashtag = useCallback((tag: string) => {
    setHashtags((prev) => prev.filter((t) => t !== tag));
  }, []);

  const handleHashtagKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      addHashtag(hashtagInput);
    } else if (e.key === 'Backspace' && !hashtagInput && hashtags.length > 0) {
      setHashtags((prev) => prev.slice(0, -1));
    }
  }, [hashtagInput, hashtags, addHashtag]);

  // Mobile keyboards often don't fire reliable keyDown events for space/comma.
  // Catch delimiters in onChange as a fallback so tags are added on mobile too.
  const handleHashtagChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    if (val.includes(' ') || val.includes(',')) {
      // Split on delimiters, add each non-empty segment as a tag
      const parts = val.split(/[\s,]+/);
      for (const part of parts) {
        if (part.trim()) addHashtag(part);
      }
    } else {
      setHashtagInput(val);
    }
  }, [addHashtag]);

  const handleCropDone = useCallback((croppedFile: File, croppedPreviewUrl: string) => {
    setSelectedFile(croppedFile);
    setPreview(croppedPreviewUrl);
    setIsCropping(false);
    // Clean up the raw image URL
    if (rawImageUrl) URL.revokeObjectURL(rawImageUrl);
    setRawImageUrl(null);
  }, [rawImageUrl]);

  const handleCropSkip = useCallback(() => {
    // Use the original file as-is
    if (rawImageUrl) {
      setPreview(rawImageUrl);
      setRawImageUrl(null);
    }
    setIsCropping(false);
  }, [rawImageUrl]);

  /** Re-enter cropping mode from the final preview — uses the original un-cropped file */
  const handleRecrop = useCallback(() => {
    const source = originalFile ?? selectedFile;
    if (!source) return;
    const url = URL.createObjectURL(source);
    // If the current preview is a blob URL we created, revoke it
    if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
    setRawImageUrl(url);
    setPreview(null);
    setIsCropping(true);
  }, [originalFile, selectedFile, preview]);

  const handleClearPhoto = useCallback(() => {
    // Spin the X, then clear the photo after the animation
    if (clearSpinTimeout.current) clearTimeout(clearSpinTimeout.current);
    setClearSpin(true);
    clearSpinTimeout.current = setTimeout(() => {
      setClearSpin(false);
      if (preview && preview.startsWith('blob:')) URL.revokeObjectURL(preview);
      if (rawImageUrl) URL.revokeObjectURL(rawImageUrl);
      setPreview(null);
      setRawImageUrl(null);
      setSelectedFile(null);
      setOriginalFile(null);
      setIsCropping(false);
    }, 400);
  }, [preview, rawImageUrl]);

  const handleDevelop = async () => {
    if (!selectedFile || !user) return;

    try {
      await uploadPicture({
        file: selectedFile,
        caption: caption || undefined,
        title: title || undefined,
        hashtags: hashtags.length > 0 ? hashtags : undefined,
        contentWarning: cwEnabled ? cwReason : undefined,
      });
      setFlash(true);
    } catch (error) {
      console.error('Upload failed:', error);
      const message = error instanceof Error ? error.message : 'Unknown error';
      alert(`Upload failed: ${message}`);
    }
  };

  const handleFlashComplete = useCallback(() => {
    setFlash(false);
    navigate('/');
  }, [navigate]);

  const insertEmojiAtCursor = (emoji: string, inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>, setValue: React.Dispatch<React.SetStateAction<string>>) => {
    const el = inputRef.current;
    if (el) {
      const start = el.selectionStart ?? el.value.length;
      const end = el.selectionEnd ?? el.value.length;
      const newValue = el.value.slice(0, start) + emoji + el.value.slice(end);
      setValue(newValue);
      // Restore cursor position after the emoji
      requestAnimationFrame(() => {
        el.selectionStart = el.selectionEnd = start + emoji.length;
        el.focus();
      });
    } else {
      setValue(prev => prev + emoji);
    }
  };

  if (!user) {
    return (
      <AppShell hideBottomNav>
        <div className="flex flex-col items-center justify-center py-20 text-center gap-6">
          <div className="bg-polaroid-white rounded-[3px] p-4 pb-12 border-2 border-dashed border-cream-dark shadow-sm w-48" style={{ transform: 'rotate(2deg)' }}>
            <div className="aspect-[4/5] flex items-center justify-center bg-cream-dark/50 rounded-sm">
              <Camera size={40} className="text-pol-text-secondary/40" />
            </div>
            <p className="font-hand text-pol-text-secondary text-center mt-2 text-lg">Log in to post</p>
          </div>
          <LoginArea className="max-w-60" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell hideBottomNav>
      <CameraFlash trigger={flash} onComplete={handleFlashComplete} />

      <div className="py-6 space-y-5">
        {/* ── State 1: No image selected — empty placeholder ── */}
        {!preview && !isCropping && (
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full bg-polaroid-white rounded-[3px] p-3 pb-0 border-2 border-dashed border-pol-pink/30 shadow-sm transition-transform active:scale-[0.98] relative"
          >
            <div className="aspect-[4/5] flex flex-col items-center justify-center gap-3 bg-cream-dark/30 rounded-sm">
              <ImagePlus size={48} className="text-pol-text-secondary/40" strokeWidth={1.2} />
              <p className="font-sans text-pol-text-secondary text-sm">Tap to select a photo</p>
            </div>
            <div className="px-1 pt-2 pb-3">
              <p className="font-hand text-pol-text-secondary/40 text-[17px]">Your title here...</p>
              <p className="font-hand font-bold text-sm text-pol-text-caption/40">{todayDate}</p>
            </div>
          </button>
        )}

        {/* ── State 2: Cropping mode — interactive cropper ── */}
        {isCropping && rawImageUrl && (
          <div className="relative bg-polaroid-white rounded-[3px] p-3 pb-3 shadow-[0_3px_12px_rgba(45,27,78,0.12)]">
            <WashiTape eventId={seed} />
            <ImageCropper
              imageSrc={rawImageUrl}
              onCropDone={handleCropDone}
              onCancel={handleCropSkip}
            />
          </div>
        )}

        {/* ── State 3: Final preview with cropped (or skipped) image ── */}
        {preview && !isCropping && (
          <div className="relative bg-polaroid-white rounded-[3px] p-3 pb-0 shadow-[0_3px_12px_rgba(45,27,78,0.12)]">
            {/* Washi tape */}
            <WashiTape eventId={seed} />

            {/* Photo with clear + re-crop buttons */}
            <div className="relative">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full aspect-[4/5] overflow-hidden bg-cream-dark block"
              >
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              </button>

              {/* X doodle button — paper-dot style, spins on click then clears the photo */}
              <button
                onClick={handleClearPhoto}
                className="absolute top-2 right-2 z-20"
                aria-label="Remove photo"
              >
                <span
                  className={cn(hasBgImage ? 'paper-dot' : 'paper-dot-pastel', 'inline-flex items-center justify-center')}
                  style={{
                    width: 32,
                    height: 32,
                    display: 'inline-flex',
                    transform: `rotate(${clearSpin ? 360 : 0}deg)`,
                    transition: clearSpin ? 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
                  }}
                >
                  <X size={18} className="text-pol-text" strokeWidth={2.5} />
                </span>
              </button>

              {/* Re-crop button — bottom-left */}
              <button
                onClick={handleRecrop}
                className="absolute bottom-2 left-2 z-20"
                aria-label="Re-crop photo"
              >
                <span
                  className={cn(hasBgImage ? 'paper-dot' : 'paper-dot-pastel', 'inline-flex items-center justify-center')}
                  style={{ width: 32, height: 32, display: 'inline-flex' }}
                >
                  <Crop size={16} className="text-pol-text" strokeWidth={2} />
                </span>
              </button>
            </div>

            {/* Title + date inside the polaroid white strip */}
            <div className="px-1 pt-2 pb-3">
              {/* Title input — looks like handwriting on the polaroid */}
              <div className="relative flex items-center gap-1">
                {/* Shortcode suggestions for title */}
                {titleShortcode.showSuggestions && (
                  <div className="absolute left-0 right-0 z-50" style={{ bottom: '100%', marginBottom: '4px' }}>
                    <EmojiSuggestions
                      suggestions={titleShortcode.suggestions}
                      query={titleShortcode.query}
                      onSelect={(emoji) => {
                        const el = titleInputRef.current;
                        const cursorPos = el?.selectionStart ?? title.length;
                        const { newValue, newCursor } = titleShortcode.selectSuggestion(emoji, title, cursorPos);
                        setTitle(newValue);
                        requestAnimationFrame(() => {
                          if (el) {
                            el.selectionStart = el.selectionEnd = newCursor;
                            el.focus();
                          }
                        });
                      }}
                    />
                  </div>
                )}
                <input
                  ref={titleInputRef}
                  type="text"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    titleShortcode.handleChange(e.target.value, e.target.selectionStart ?? e.target.value.length);
                  }}
                  onBlur={() => setTimeout(titleShortcode.dismiss, 200)}
                  placeholder="Give it a name..."
                  className="flex-1 bg-transparent outline-none font-hand text-[17px] text-pol-text placeholder:text-pol-text-secondary/40 leading-tight"
                />
                <Popover open={titleEmojiOpen} onOpenChange={setTitleEmojiOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex-shrink-0 w-7 h-7 flex items-center justify-center hover:bg-cream-dark/60 transition-colors text-pol-text-secondary/50 hover:text-pol-text-secondary"
                    >
                      <Smile size={16} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-0 shadow-xl" side="bottom" align="end">
                    <EmojiPicker onSelect={(emoji) => {
                      insertEmojiAtCursor(emoji, titleInputRef, setTitle);
                      setTitleEmojiOpen(false);
                    }} />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Date — blue ink, just like the final card */}
              <p className="font-hand font-bold text-sm text-pol-text-caption mt-0.5">
                {todayDate}
              </p>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,image/avif"
          onChange={handleFileSelect}
          className="hidden"
        />

        {preview && !isCropping && (
          <>
            {/* Caption input — lined paper, separate from card */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={cn(
                  'text-[13px] font-semibold uppercase tracking-wide text-pol-text',
                  hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
                )}>
                  Add Caption
                </label>
                <Popover open={captionEmojiOpen} onOpenChange={setCaptionEmojiOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="flex-shrink-0 w-7 h-7 flex items-center justify-center hover:bg-cream-dark/60 transition-colors text-pol-text-secondary/50 hover:text-pol-text-secondary"
                    >
                      <Smile size={16} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 border-0 shadow-xl" side="bottom" align="end">
                    <EmojiPicker onSelect={(emoji) => {
                      insertEmojiAtCursor(emoji, captionInputRef, setCaption);
                      setCaptionEmojiOpen(false);
                    }} />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="relative">
                {/* Shortcode suggestions for caption */}
                {captionShortcode.showSuggestions && (
                  <div className="absolute left-0 right-0 z-50" style={{ bottom: '100%', marginBottom: '4px' }}>
                    <EmojiSuggestions
                      suggestions={captionShortcode.suggestions}
                      query={captionShortcode.query}
                      onSelect={(emoji) => {
                        const el = captionInputRef.current;
                        const cursorPos = el?.selectionStart ?? caption.length;
                        const { newValue, newCursor } = captionShortcode.selectSuggestion(emoji, caption, cursorPos);
                        setCaption(newValue);
                        requestAnimationFrame(() => {
                          if (el) {
                            el.selectionStart = el.selectionEnd = newCursor;
                            el.focus();
                          }
                        });
                      }}
                    />
                  </div>
                )}
                <textarea
                  ref={captionInputRef}
                  value={caption}
                  onChange={(e) => {
                    setCaption(e.target.value);
                    captionShortcode.handleChange(e.target.value, e.target.selectionStart ?? e.target.value.length);
                  }}
                  onBlur={() => setTimeout(captionShortcode.dismiss, 200)}
                  placeholder="Write something..."
                  rows={3}
                  className="w-full bg-white lined-paper font-hand text-lg text-pol-text py-3 px-3 outline-none resize-none placeholder:text-pol-text-secondary/40 border border-cream-dark focus:border-pol-pink/50 transition-colors"
                />
              </div>
            </div>

            {/* Hashtag input */}
            <div>
              <label className={cn(
                'text-[13px] font-semibold uppercase tracking-wide text-pol-text mb-1.5 flex items-center gap-1.5',
                hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
              )}>
                <Hash size={13} className="text-pol-text-secondary" />
                Add Tags
                <span className="text-pol-text-secondary/50 font-normal normal-case tracking-normal text-[11px]">
                  (optional)
                </span>
              </label>

              {/* Tag pills */}
              <div className="flex flex-wrap items-center gap-1.5 min-h-[36px] bg-white border border-cream-dark focus-within:border-pol-pink/50 transition-colors px-2.5 py-2">
                {hashtags.map((tag) => {
                  const { bg, text } = getHashtagColor(tag);
                  return (
                    <span
                      key={tag}
                      className="inline-flex items-center gap-1 font-hand text-[14px] px-2 py-0.5"
                      style={{ backgroundColor: bg, color: text }}
                    >
                      #{tag}
                      <button
                        onClick={() => removeHashtag(tag)}
                        className="hover:opacity-70 transition-opacity"
                        type="button"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
                {hashtags.length < 5 && (
                  <input
                    ref={hashtagInputRef}
                    type="text"
                    value={hashtagInput}
                    onChange={handleHashtagChange}
                    onKeyDown={handleHashtagKeyDown}
                    onBlur={() => { if (hashtagInput.trim()) addHashtag(hashtagInput); }}
                    placeholder={hashtags.length === 0 ? 'sunset, travel, nostr...' : ''}
                    className="flex-1 min-w-[80px] bg-transparent outline-none font-hand text-[14px] text-pol-text placeholder:text-pol-text-secondary/40"
                  />
                )}
              </div>
              <p className={cn(
                'text-[11px] text-pol-text-secondary/50 mt-1 pl-0.5',
                hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
              )}>
                Press space or enter to add. Max 5 tags.
              </p>
            </div>

            {/* Content Warning toggle */}
            <div>
              <button
                type="button"
                onClick={() => setCwEnabled(!cwEnabled)}
                className={cn(
                  'flex items-center gap-2 text-[13px] font-semibold uppercase tracking-wide transition-colors',
                  hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
                  cwEnabled ? 'text-pol-text' : 'text-pol-text-secondary',
                )}
              >
                <EyeOff size={13} className={cwEnabled ? 'text-pol-pink' : 'text-pol-text-secondary'} />
                Content Warning
                <span className="text-pol-text-secondary/50 font-normal normal-case tracking-normal text-[11px]">
                  (optional)
                </span>
              </button>

              {cwEnabled && (
                <div className="mt-1.5">
                  <input
                    type="text"
                    value={cwReason}
                    onChange={(e) => setCwReason(e.target.value)}
                    placeholder="Reason (e.g. nudity, spoiler)..."
                    className="w-full bg-white font-hand text-[15px] text-pol-text py-2 px-3 outline-none border border-cream-dark focus:border-pol-pink/50 transition-colors placeholder:text-pol-text-secondary/40"
                  />
                  <p className={cn(
                    'text-[11px] text-pol-text-secondary/50 mt-1 pl-0.5',
                    hasBgImage ? 'paper-strip' : 'paper-strip-pastel',
                  )}>
                    This photo will be hidden until viewers choose to reveal it.
                  </p>
                </div>
              )}
            </div>

            {/* POST button — polaroid white card with washi tape, themed accent */}
            <button
              onClick={handleDevelop}
              disabled={isPending}
              className={cn(
                'relative w-full bg-polaroid-white py-4 shadow-[0_3px_12px_rgba(45,27,78,0.12)]',
                'transition-all active:scale-[0.97] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(45,27,78,0.15)]',
                'disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_3px_12px_rgba(45,27,78,0.12)]',
              )}
              style={{ transform: 'rotate(-0.8deg)' }}
            >
              {/* Decorative tape strip across the top */}
              <div
                className="absolute top-0 left-1/2 z-10 w-[100px] h-[22px] tape-rainbow"
                style={{ transform: 'translateX(-50%) translateY(-11px) rotate(2deg)' }}
              />

              <span className="relative z-10 flex items-center justify-center font-hand text-2xl font-bold text-pol-text tracking-wide">
                {isPending ? (
                  <span className="animate-pulse">Developing...</span>
                ) : (
                  <span>POST</span>
                )}
              </span>

              {/* Themed accent bar at bottom */}
              <div className="absolute bottom-0 left-0 right-0 h-[6px] bg-pol-pink opacity-60" />
            </button>
          </>
        )}
      </div>
    </AppShell>
  );
}
